import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateLeft } from '@fortawesome/pro-solid-svg-icons';
import auth, { isProduction } from '@client/components/auth';
import { useResetOnboardingMutation } from 'generated/graphql';
import { PATHS } from '@client/routes';
import { navigate } from '@client/new/helpers/history';
import * as styles from './resetOnboardingButton.css';

type ResetOnboardingButtonProps = {
  creatorId: number;
};

const shouldShowResetButton = (): boolean =>
  !isProduction() || auth.isAdministrator();

export const ResetOnboardingButton = ({
  creatorId,
}: ResetOnboardingButtonProps) => {
  const [isResetting, setIsResetting] = useState(false);
  const [resetOnboardingMutation] = useResetOnboardingMutation();

  if (!shouldShowResetButton()) {
    return null;
  }

  const handleReset = async () => {
    if (isResetting) return;

    const confirmed = window.confirm(
      'Reset onboarding? This will clear all your onboarding progress and responses.',
    );

    if (!confirmed) return;

    setIsResetting(true);
    try {
      await resetOnboardingMutation({
        variables: { creatorId: String(creatorId) },
      });

      const onboardingPath = auth.isAdministrator()
        ? PATHS.ONBOARDING_ADMIN(creatorId)
        : PATHS.ONBOARDING;
      navigate(onboardingPath);
      window.location.reload();
    } catch (error) {
      console.error('Failed to reset onboarding:', error);
      alert('Failed to reset onboarding. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <button
      type="button"
      className={styles.resetButton}
      onClick={handleReset}
      disabled={isResetting}
      data-testid="reset-onboarding-nav-button"
      title="Reset onboarding (integration only)"
    >
      <FontAwesomeIcon
        icon={faRotateLeft}
        className={isResetting ? styles.spinning : undefined}
      />
      <span>{isResetting ? 'Resetting...' : 'Reset Onboarding'}</span>
    </button>
  );
};
